import { Trans, useTranslation } from 'react-i18next';

import {
    Anchor,
    Body1,
    Body2,
    Flex,
    Heading2,
    Heading4,
    RichText,
    Stack,
    Subtitle1,
} from '@myphx-engineering/phx-design-system';

import type {
    PathwayCardContent,
    ProductDisclaimer,
} from '../../../../configuration/shared';
import type { PrescriptionOffer } from '../../../../hooks/use-prescription-offers';
import { ConditionalComponent } from '../../../utility/ConditionalComponent';

import { FallbackSubcard } from './FallbackSubcard';

interface CardPriceProps {
    content: PathwayCardContent;
    offer: PrescriptionOffer | undefined;
    displayPrice?: number | null;
    fallbackPrice?: number | null;
    fallbackDisclaimer?: string | null;
    disclaimer?: string | null;
    disclaimerV2?: ProductDisclaimer[];
    termsUrl?: string | null;
    testIdPrefix: string;
}

export const CardPrice = ({
    content,
    offer,
    displayPrice,
    fallbackPrice,
    fallbackDisclaimer,
    disclaimer,
    disclaimerV2 = [],
    termsUrl,
    testIdPrefix,
}: CardPriceProps) => {
    const { t } = useTranslation('lilly');

    const resolvedPrice = displayPrice ?? offer?.patientCost;

    if (resolvedPrice == null || !content.showPrice) {
        return (
            <Subtitle1 data-testid={`${testIdPrefix}-price`}>
                {content.noPriceText ?? t('fill.pathSelect.cards.line2NoPrice')}
            </Subtitle1>
        );
    }

    const hasSavingsCard =
        offer?.headlinePriceType === 'CashAssistance' ||
        offer?.headlinePriceType === 'CopayAssistance' ||
        offer?.headlinePriceType === 'Incentive';

    const showFallbackSubcard =
        !!content.fallbackPriceLabel && fallbackPrice != null;

    const showRichDisclaimer = !!disclaimerV2?.length;

    const hasTerms =
        (!!disclaimer && !showRichDisclaimer) ||
        showRichDisclaimer ||
        !!termsUrl ||
        (hasSavingsCard && !!content.priceWithCardNote);

    return (
        <Stack gap="xxs">
            <Stack gap="xxs" data-testid={`${testIdPrefix}-price`}>
                <ConditionalComponent condition={!!content.priceLabel}>
                    <Body1>{content.priceLabel}</Body1>
                </ConditionalComponent>
                <Flex align="baseline" gap={0}>
                    <Heading4 size="2rem">${resolvedPrice}</Heading4>
                    <Heading2>
                        {t('fill.pathSelect.cards.perMonth')}
                        {content.priceSuperscript}
                    </Heading2>
                </Flex>
                <RichText text={content.priceNote ?? ''} />
                {showFallbackSubcard ? (
                    <FallbackSubcard
                        label={content.fallbackPriceLabel!}
                        price={fallbackPrice!}
                        superscript={content.fallbackSuperscript}
                        disclaimer={fallbackDisclaimer}
                        termsUrl={content.fallbackTermsUrl}
                    />
                ) : (
                    <ConditionalComponent condition={!!fallbackDisclaimer}>
                        <Body2>
                            {content.fallbackSuperscript}
                            {fallbackDisclaimer}
                        </Body2>
                    </ConditionalComponent>
                )}
            </Stack>
            <ConditionalComponent condition={hasTerms}>
                <Stack gap="xxs" data-testid={`${testIdPrefix}-terms`}>
                    <ConditionalComponent
                        condition={!!disclaimer && !showRichDisclaimer}
                    >
                        <Body2>
                            {content.priceSuperscript}
                            {disclaimer}
                        </Body2>
                    </ConditionalComponent>
                    <ConditionalComponent condition={showRichDisclaimer}>
                        {disclaimerV2!.map((richDisclaimer) => {
                            const richContent = richDisclaimer.content;

                            return richContent.map(({ key, ...richLine }) => (
                                <RichText key={key} {...richLine} />
                            ));
                        })}
                    </ConditionalComponent>
                    <ConditionalComponent condition={!!termsUrl}>
                        <Body2>
                            <Trans
                                i18nKey="fill.pricing.seeTerms"
                                components={{
                                    1: (
                                        <Anchor
                                            href={termsUrl!}
                                            target="_blank"
                                        />
                                    ),
                                }}
                            />
                        </Body2>
                    </ConditionalComponent>
                    <ConditionalComponent
                        condition={
                            hasSavingsCard && !!content.priceWithCardNote
                        }
                    >
                        <Body2>{content.priceWithCardNote}</Body2>
                    </ConditionalComponent>
                </Stack>
            </ConditionalComponent>
        </Stack>
    );
};
